<template>
  <div>
    <v-app-bar flat color="transparent" class="main-appbar">
      <app-bar-nav-icon />
      <v-skeleton-loader
        type="chip"
        transition="fade-transition"
        v-if="loading"
      ></v-skeleton-loader>

      <v-toolbar-title v-else class="headline font-weight-bold"
        >Requests ({{ listMeta.total }})</v-toolbar-title
      >

      <v-spacer></v-spacer>
      <v-btn
        v-show="!loading && isFiltered"
        rounded
        color="error lighten-1"
        class="mr-6"
        @click="clearFilters"
      >
        <span class="white--text mb-1">Clear Filter</span>
        <v-icon right>
          {{ icons.cancel }}
        </v-icon>
      </v-btn>
      <v-row v-show="showSearch" align="center" style="max-width: 410px;">
        <v-text-field
          v-if="!loading"
          class="search"
          ref="searchField"
          flat
          hide-details
          solo
          rounded
          clearable
          background-color="grey lighten-3"
          label="Search file owner, file name or file template"
          v-model="filter.search"
          @input="search"
        ></v-text-field>
      </v-row>

      <v-skeleton-loader
        type="chip"
        transition="fade-transition"
        v-if="loading"
      ></v-skeleton-loader>

      <template v-else>
        <v-btn class="ml-5" icon @click="showSearch = !showSearch">
          <img src="@/assets/icons/search.svg" alt height="17.7px" />
        </v-btn>
      </template>
    </v-app-bar>

    <div
      class="mt-12 mx-4"
      id="user-list"
      v-infinite-scroll="loadMore"
      :infinite-scroll-disabled="loading"
      infinite-scroll-distance="100"
    >
      <v-simple-table>
        <thead>
          <tr>
            <th width="20%" colspan="2" class="text-left pa-0">File Owner</th>
            <th width="20%" class="text-left">
              <v-menu offset-y>
                <template #activator="{ on, attrs }">
                  <div v-bind="attrs" v-on="on">
                    File Name
                    <v-icon>
                      {{ icons.menu }}
                    </v-icon>
                  </div>
                </template>
                <v-list :min-width="230">
                  <v-radio-group v-model="sort" hide-details class="mt-0 py-0">
                    <div v-for="(item, key) in fileNameSort" :key="key">
                      <v-list-item inactive>
                        <v-list-item-title>{{ item.text }}</v-list-item-title>
                        <v-list-item-action @click="updateFetchRequests">
                          <v-radio :value="item.value" />
                        </v-list-item-action>
                      </v-list-item>
                      <v-divider v-if="key !== fileNameSort.length - 1" />
                    </div>
                  </v-radio-group>
                </v-list>
              </v-menu>
            </th>
            <th width="15%" class="text-left">
              <v-menu offset-y>
                <template #activator="{ on, attrs }">
                  <div v-bind="attrs" v-on="on">
                    File Template
                    <v-icon right>
                      {{ icons.menu }}
                    </v-icon>
                  </div>
                </template>
                <v-list :min-width="230">
                  <v-radio-group
                    v-model="filter.template"
                    hide-details
                    class="mt-0 py-0"
                  >
                    <div v-for="(filter, key) in templatesFilters" :key="key">
                      <v-list-item inactive>
                        <v-list-item-title>{{
                          filter.label
                        }}</v-list-item-title>
                        <v-list-item-action @click="updateFetchRequests">
                          <v-radio :value="key + 1" />
                        </v-list-item-action>
                      </v-list-item>
                      <v-divider v-if="key !== templatesFilters.length - 1" />
                    </div>
                  </v-radio-group>
                </v-list>
              </v-menu>
            </th>
            <th width="20%" class="text-left">
              <v-menu
                v-model="datePicker"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template #activator="{ on, attrs }">
                  <div v-bind="attrs" v-on="on">
                    Date Requested
                    <v-icon>
                      {{ icons.menu }}
                    </v-icon>
                  </div>
                </template>
                <v-date-picker
                  v-model="selectedDate"
                  @change="filterDateRequests"
                ></v-date-picker>
              </v-menu>
            </th>
            <th width="10%" class="text-left">
              <v-menu offset-y>
                <template #activator="{ on, attrs }">
                  <div v-bind="attrs" v-on="on">
                    Status
                    <v-icon right>
                      {{ icons.menu }}
                    </v-icon>
                  </div>
                </template>
                <v-list :min-width="230">
                  <v-radio-group
                    v-model="filter.status"
                    hide-details
                    class="mt-0 py-0"
                  >
                    <div v-for="(filter, key) in fileStatusFilters" :key="key">
                      <v-list-item inactive>
                        <v-list-item-title>{{ filter.text }}</v-list-item-title>
                        <v-list-item-action @click="updateFetchRequests">
                          <v-radio :value="filter.value" />
                        </v-list-item-action>
                      </v-list-item>
                      <v-divider v-if="key !== fileStatusFilters.length - 1" />
                    </div>
                  </v-radio-group>
                </v-list>
              </v-menu>
            </th>
            <th width="15%" class="text-left">Action</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="request in requests"
            :key="request.id"
            class="clickable"
            @click.stop="showRequest(request)"
          >
            <td colspan="2" class="text-left pl-0">
              <user-avatar
                :avatar="request.user.avatar"
                size="40"
                class="mr-12"
              />
              {{ request.user.full_name }}
            </td>
            <td>{{ request.name }}</td>
            <td>
              {{
                request.template
                  ? request.template.label
                  : 'Custom - No Template'
              }}
            </td>
            <td>{{ request.addedDate }}</td>
            <td>
              <v-chip
                class="ma-2"
                :color="
                  request.status === 'valid'
                    ? '#D1FFEF'
                    : request.status === 'rejected'
                    ? '#FFE2E2'
                    : '#FFF3CC'
                "
              >
                <span
                  class="text-capitalize"
                  :class="
                    request.status === 'valid'
                      ? 'green--text'
                      : request.status === 'rejected'
                      ? 'red--text'
                      : 'yellow--text text--darken-4'
                  "
                  >{{ request.status }}</span
                >
              </v-chip>
            </td>
            <td>
              <v-btn
                color="primary"
                @click.stop="
                  $router.push({
                    name: 'request.details',
                    params: { id: request.id }
                  })
                "
                >See Full Details</v-btn
              >
            </td>
          </tr>
          <template v-if="loading">
            <tr v-for="index in 10" :key="index + '-skeleton'">
              <td class="py-3 pl-0" width="5%">
                <v-skeleton-loader
                  class="avatar-loading--36"
                  type="avatar"
                  transition="fade-transition"
                />
              </td>
              <td class="pl-0">
                <v-skeleton-loader
                  type="list-item"
                  transition="fade-transition"
                />
              </td>
              <td class="pl-0">
                <v-skeleton-loader
                  type="list-item"
                  transition="fade-transition"
                />
              </td>
              <td class="pl-0">
                <v-skeleton-loader
                  type="list-item"
                  transition="fade-transition"
                />
              </td>
              <td class="pl-0">
                <v-skeleton-loader
                  type="list-item"
                  transition="fade-transition"
                />
              </td>
              <td class="pl-0">
                <v-skeleton-loader
                  type="list-item"
                  transition="fade-transition"
                />
              </td>
              <td class="pl-0">
                <v-skeleton-loader
                  type="list-item"
                  transition="fade-transition"
                />
              </td>
            </tr>
          </template>
        </tbody>
      </v-simple-table>

      <div
        class="d-flex justify-center mt-3"
        v-if="!(requests.length > 0) && !loading"
      >
        <p>No requests found.</p>
      </div>
    </div>

    <RequestModal
      v-model="showRequestModal"
      :request="selectedRequest"
      @showResponse="showResponse"
    />

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" right>
      {{ snackbar.message }}
      <v-btn color="white" text @click="snackbar.show = false">Ok</v-btn>
    </v-snackbar>
  </div>
</template>

<script>
import { mapState, mapActions, mapMutations } from 'vuex'
import {
  mdiDotsVertical,
  mdiUnfoldMoreHorizontal,
  mdiMagnify,
  mdiAccountCircleOutline,
  mdiChevronRight,
  mdiMenuDown,
  mdiClose
} from '@mdi/js'
import ControlsMixin from '@/utils/mixins/Controls'
import TemplatesMixin from '@/utils/mixins/Templates'
import AppBarNavIcon from '@/layouts/shared/AppBarNavIcon'
import UserAvatar from '@/components/UserAvatar'
import RequestModal from './components/RequestModal'
import infiniteScroll from 'vue-infinite-scroll'
import debounce from 'lodash/debounce'
import { each } from 'lodash'

export default {
  name: 'RequestsPage',

  mixins: [ControlsMixin, TemplatesMixin],

  directives: {
    infiniteScroll
  },

  components: {
    UserAvatar,
    AppBarNavIcon,
    RequestModal
  },

  data() {
    return {
      loading: false,
      showSearch: true,
      selectedRequest: null,
      showRequestModal: false,
      sort: null,
      filter: {
        search: '',
        status: 'pending approval,valid,rejected',
        template: null
      },
      icons: {
        settings: mdiDotsVertical,
        sort: mdiUnfoldMoreHorizontal,
        search: mdiMagnify,
        view: mdiAccountCircleOutline,
        arrow: mdiChevronRight,
        menu: mdiMenuDown,
        cancel: mdiClose
      },
      snackbar: {
        show: false,
        message: null,
        color: null
      },
      dateSort: [
        { value: 'created_at', text: 'Ascending' },
        { value: '-created_at', text: 'Descending' }
      ],
      fileNameSort: [
        { value: 'name', text: 'Ascending' },
        { value: '-name', text: 'Descending' }
      ],
      fileStatusFilters: [
        { value: 'pending approval', text: 'Pending Approval' },
        { value: 'valid', text: 'Valid' },
        { value: 'rejected', text: 'Rejected' }
      ],
      datePicker: false,
      selectedDate: null,
      isFiltered: false
    }
  },

  created() {
    this.clearRequests()
    this.fetchRequests(1)
  },

  computed: {
    ...mapState({
      requests: state => state.request.list,
      listMeta: state => state.request.listMeta
    }),
    templatesFilters() {
      let templates = this.templates
      templates.push({ label: 'Custom - No Template' }, { label: 'All' })
      return templates
    }
  },

  methods: {
    ...mapActions({
      getRequests: 'request/getRequests',
      deleteRequest: 'request/deleteRequest'
    }),

    ...mapMutations({
      clearRequests: 'request/clearRequestList'
    }),

    categoryTemplates(categories) {
      const templates = []
      each(categories, category => {
        templates.push(category.label)
      })
      return templates.join()
    },

    pageChanged(page) {
      this.fetchRequests(page)
    },

    loadMore() {
      if (this.listMeta.current_page < this.listMeta.last_page) {
        this.fetchRequests(this.listMeta.current_page + 1)
      }
    },

    search: debounce(function() {
      this.clearRequests()
      this.fetchRequests()
    }, 600),

    async fetchRequests(page = 1) {
      if (this.loading) return
      let params = { page }
      if (this.filter.search) {
        params.search = this.filter.search
      }
      if (this.sort) {
        params.sort = this.sort
      }
      if (this.selectedDate) {
        params.searchDate = this.selectedDate
      }
      if (this.filter.template) {
        const template = this.filter.template
        params.template =
          template < 11 ? template : template === 11 ? null : undefined
      }
      params.status = this.filter.status

      this.loading = true
      await this.getRequests(params)
      this.loading = false
    },

    updateFetchRequests() {
      this.isFiltered = true
      this.clearRequests()
      this.fetchRequests()
    },

    filterDateRequests: debounce(function() {
      this.isFiltered = true
      this.datePicker = false
      this.clearRequests()
      this.fetchRequests()
    }, 600),

    clearFilters() {
      this.sort = null
      this.selectedDate = null
      this.filter.status = 'pending approval,valid,rejected'
      this.filter.template = null
      this.isFiltered = false
      this.clearRequests()
      this.fetchRequests()
    },

    showRequest(request) {
      this.selectedRequest = request
      this.showRequestModal = true
    },

    showResponse(status) {
      if (status === 'valid') {
        this.showSnackbar('Accepted request successfully', 'success')
      } else if (status === 'pending approval') {
        this.showSnackbar('Changed to pending request successfully', 'warning')
      } else {
        this.showSnackbar('Rejected request successfully', 'error')
      }
      this.showRequestModal = false
      this.clearRequests()
      this.fetchRequests()
    },

    showSnackbar(message, color) {
      this.snackbar.message = message
      this.snackbar.color = color
      this.snackbar.show = true
    },

    async showSearchField() {
      this.showSearch = !this.showSearch
      await this.$nextTick()
      this.$refs.searchField.$refs.input.focus()
    }
  }
}
</script>
